import { useFormik } from 'formik'
import * as Yup from 'yup'
import ModalComponent from '..'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useEditTeamMemberMutation,
  useGetRolesQuery,
  useInviteTeamMemberMutation,
} from '../../../redux-toolkits/user/user.slice'
import { TeamMember } from '../../../redux-toolkits/user/user.type'
import { Button } from '../../button'
import { ActionChip } from '../../cards/actionChip'
import ComboBox from '../../inputs/combobox'
import { EditInput } from '../../inputs/editInput'
import { TextInput } from '../../inputs/textInput'
import { SelectFulfilmentCenterModal } from './selectFulfilmentCenter'

export const CreateTeammateModal = ({
  open,
  closeModal,
  teamMember,
  onSuccess,
}: {
  open: boolean
  closeModal: () => void
  teamMember?: TeamMember
  onSuccess: () => void
}) => {
  const { isLoading, data: roles } = useGetRolesQuery({
    skip: 0,
    limit: 100,
  })

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const [inviteTeamMember, { isLoading: invitingTeamMember }] =
    useInviteTeamMemberMutation()

  const [editTeamMember, { isLoading: updatingTeamMember }] =
    useEditTeamMemberMutation()

  const {
    values,
    handleBlur,
    handleChange,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    resetForm,
    validateForm,
  } = useFormik({
    initialValues: {
      firstname: teamMember?.firstname ?? '',
      lastname: teamMember?.lastname ?? '',
      email: teamMember?.email ?? '',
      roleId: teamMember?.role._id ?? '',
      fulfillmentCenter: teamMember?.fulfillmentCenter?.map((c) => c._id) ?? [],
      fulfillmentCenters: teamMember?.fulfillmentCenter ?? [],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      firstname: Yup.string()
        .matches(/^[A-Za-z]+$/, 'No numbers or special characters allowed')
        .required('Required'),
      lastname: Yup.string()
        .matches(/^[A-Za-z]+$/, 'No numbers or special characters allowed')
        .required('Required'),
      email: Yup.string().email('Invalid email address').required('Required'),
      roleId: Yup.string().required('Required'),
      fulfillmentCenter: Yup.array()
        .of(Yup.string())
        .min(1, 'Select at least one fulfilment center'),
    }),
    onSubmit: async (vals) => {
      const { fulfillmentCenters, ...values } = vals

      if (teamMember !== null && teamMember !== undefined) {
        const resp = await editTeamMember({
          payload: {
            ...values,
            fulfillmentCenter: values.fulfillmentCenter,
          },
          id: teamMember._id,
        })

        if (resp.data?.success) {
          onSuccess()
        }
      } else {
        const resp = await inviteTeamMember({
          ...values,
          fulfillmentCenter: values.fulfillmentCenter,
        })

        if (resp.data?.success) {
          onSuccess()
        }
      }
    },
  })

  return (
    <>
      <ModalComponent
        title={`${teamMember ? 'Edit' : 'Invite new'} team member`}
        open={open}
        setOpen={() => {}}
        onCloseCallback={() => {
          closeModal()
          resetForm()
        }}
        childrenClassName="md:!max-w-[420px]"
      >
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4 ">
          <TextInput
            type="text"
            name="firstname"
            placeholder="First name"
            value={values.firstname}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={
              errors.firstname && touched.firstname ? errors.firstname : ''
            }
            hasIcon={false}
            title="First name"
            inputClass="!capitalize"
          />

          <TextInput
            type="text"
            name="lastname"
            placeholder="Last name"
            value={values.lastname}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={errors.lastname && touched.lastname ? errors.lastname : ''}
            hasIcon={false}
            title="Last name"
            inputClass="!capitalize"
          />

          <TextInput
            type="text"
            name="email"
            placeholder="Email"
            value={values.email}
            onBlur={handleBlur}
            onChange={handleChange}
            errors={errors.email && touched.email ? errors.email : ''}
            hasIcon={false}
            containerClassName="col-span-full"
            title="Email"
          />

          <ComboBox
            type="text"
            name="roleId"
            placeholder="Select role"
            value={
              roles?.data?.find((role) => role._id === values.roleId)?.name
            }
            onChange={() => {}}
            onBlur={handleBlur}
            hasIcon={false}
            errors={errors.roleId && touched.roleId ? errors.roleId : ''}
            menuItems={
              roles?.data?.map((role) => {
                return {
                  name: role.name,
                  value: role._id,
                }
              }) ?? []
            }
            onSelectOption={(selected) => {
              setFieldValue('roleId', selected)
            }}
            title="Role"
            onSearch={() => {}}
            loading={isLoading}
            immediate
            position="bottom start"
            menuClassName="!max-h-[250px] shadow-md !z-[999]"
            containerClassName="col-span-full"
            className="col-span-full"
            isDropdown
          />

          <EditInput
            title="Fulfilment Centers"
            value={undefined}
            onEditClick={() => {
              handleModalOpen('selectCenterModal')
            }}
            errors={(errors.fulfillmentCenter ?? '') as string}
            onCancel={() => {}}
            onEditSave={() => {}}
            editing={false}
            className={'col-span-full'}
          >
            {() => {
              return (
                <div className="flex flex-wrap gap-2">
                  {values.fulfillmentCenters.length > 0
                    ? values.fulfillmentCenters.map((c) => {
                        return <ActionChip name={c.name} key={c._id} />
                      })
                    : 'No fulfillment center selected'}
                </div>
              )
            }}
          </EditInput>

          <Button
            disabled={invitingTeamMember || updatingTeamMember}
            loading={invitingTeamMember || updatingTeamMember}
            type="submit"
            label="Proceed"
            primary
            className="!mt-6 !w-full col-span-full"
          />
        </form>
      </ModalComponent>

      <SelectFulfilmentCenterModal
        open={modalOpen && modal === 'selectCenterModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={async (vals, resetForm) => {
          setFieldValue(
            'fulfillmentCenter',
            vals.map((v) => v._id),
          )
          setFieldValue('fulfillmentCenters', vals)

          handleModalClose()
          resetForm()
        }}
        selectedCenters={values.fulfillmentCenters}
        loading={false}
      />
    </>
  )
}
